import { useCallback, useMemo } from "react"

import { useDemandEditState } from "~/components/demand-2/useDemandIntents"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { useUser } from "~/providers/userProvider"
import { api } from "~/utils/api"
import { showErrorToast } from "~/utils/toaster"

import type { VariantReview } from "./reviewStatus"

const NO_REVIEWS: ReadonlyMap<string, VariantReview> = new Map()

/**
 * Reviewed/Pending per variant for the route's plan, riding the merged edit-state read. Writes are
 * optimistic: no plan number moves, so nothing else is refreshed.
 */
export function useVariantReviews() {
  const { collectionIds } = useDemand2Scope()
  const { userId } = useUser()
  const utils = api.useUtils()
  const q = useDemandEditState(collectionIds)
  const { mutateAsync: save } = api.demand2.setVariantsReviewed.useMutation()
  const { mutateAsync: resetAll, isPending: isResetting } =
    api.demand2.resetVariantReviews.useMutation()

  const rows = q.data?.variantReviews
  const reviews = useMemo<ReadonlyMap<string, VariantReview>>(
    () =>
      rows
        ? new Map(
            rows.map((r) => [r.variantId, { reviewedAt: r.reviewedAt, reviewedBy: r.reviewedBy }]),
          )
        : NO_REVIEWS,
    [rows],
  )

  // Only after a failed write: the optimistic rows already match a successful one, and a refetch
  // would also hand every other edit list a fresh reference and repaint the whole grid.
  const onError = useCallback(
    (error: unknown) => {
      showErrorToast(error)
      void utils.demand2.getDemandEditState.invalidate({ collectionIds })
    },
    [utils, collectionIds],
  )

  const setReviewed = useCallback(
    (variantIds: string[], reviewed: boolean) => {
      if (variantIds.length === 0) return
      const ids = new Set(variantIds)
      utils.demand2.getDemandEditState.setData({ collectionIds }, (prev) => {
        if (!prev) return prev
        const kept = prev.variantReviews.filter((r) => !ids.has(r.variantId) || reviewed)
        if (!reviewed) return { ...prev, variantReviews: kept }
        const already = new Set(kept.map((r) => r.variantId))
        const added = variantIds
          .filter((id) => !already.has(id))
          .map((variantId) => ({ variantId, reviewedAt: new Date(), reviewedBy: userId ?? null }))
        return { ...prev, variantReviews: [...kept, ...added] }
      })
      void save({ collectionIds, variantIds, reviewed }).catch(onError)
    },
    [collectionIds, save, onError, utils, userId],
  )

  const reset = useCallback(
    () =>
      resetAll({ collectionIds })
        .then(() =>
          utils.demand2.getDemandEditState.setData({ collectionIds }, (prev) =>
            prev ? { ...prev, variantReviews: [] } : prev,
          ),
        )
        .catch(onError),
    [collectionIds, resetAll, onError, utils],
  )

  return { reviews, setReviewed, reset, isResetting }
}
